import React, { useState, useRef, useEffect, useCallback, type ChangeEvent } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import SearchIcon from '../../assets/search.svg'
import ClearIcon from '../../assets/clear.svg'
import { SearchBar } from './styles'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

interface SearchProps {
  onSearch?: (value: string) => void
  placeholder?: string
  keepExpanded?: boolean
  fillContainer?: boolean
  stayFocusOnEnter?: boolean
  onChange?: (value: string) => void
}

const Search: React.FC<SearchProps> = ({
  onSearch,
  placeholder = 'Search',
  keepExpanded = false,
  fillContainer = false,
  stayFocusOnEnter = false,
  onChange
}) => {
  const theme = useTheme()

  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [expanded, setExpanded] = useState<boolean>(!!initialSearch)
  const [prevValue, setPrevValue] = useState<string>(initialSearch)
  const [searchValue, setSearchValue] = useState<string>(initialSearch)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isMobile) {
      setExpanded(true)
    }
  }, [isMobile])

  const handleOnFocus = useCallback((): void => {
    if (!isMobile) {
      setExpanded(true)
    }
    inputRef.current?.focus()
  }, [isMobile])

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value ?? ''
    if (value !== searchValue) {
      setSearchValue(value)
      onChange?.(value)
    }
  }, [onChange, searchValue])

  const updateSearchParam = useCallback((value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('search', value)
    setSearchParams(params)
  }, [searchParams, setSearchParams])

  const removeSearchParam = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('search')
    setSearchParams(params)
  }, [searchParams, setSearchParams])

  const handleClear = useCallback((): void => {
    setSearchValue('')
    if (searchValue) {
      onChange?.('')
      onSearch?.('')
      removeSearchParam()
    }
    setPrevValue('')
    inputRef.current?.focus()
    if (!isMobile) {
      setExpanded(false)
    }
  }, [onChange, onSearch, isMobile, searchValue, removeSearchParam])

  const handleClickAway = useCallback((): void => {
    if (searchValue !== prevValue) {
      onSearch?.(searchValue)
      setPrevValue(searchValue)
      updateSearchParam(searchValue)
      removeSearchParam()
    }
    if (!searchValue && !isMobile) {
      setExpanded(false)
    }
  }, [searchValue, prevValue, onSearch, isMobile, updateSearchParam, removeSearchParam])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      if (!stayFocusOnEnter) {
        inputRef.current?.blur()
      } else {
        inputRef.current?.focus()
      }
      if (searchValue !== prevValue && searchValue) {
        onSearch?.(searchValue)
        setPrevValue(searchValue)
        updateSearchParam(searchValue)
        removeSearchParam()
      }
    }
  }, [stayFocusOnEnter, onSearch, searchValue, prevValue, updateSearchParam, removeSearchParam])

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SearchBar
        expanded={keepExpanded || expanded}
        fillContainer={fillContainer}
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        value={searchValue}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        InputProps={{
          autoComplete: 'off',
          startAdornment: (
            <InputAdornment position="start">
              <img
                src={SearchIcon}
                alt="search"
                onClick={handleOnFocus}
                style={{ cursor: 'pointer' }}
              />
            </InputAdornment>
          ),
          endAdornment:
            expanded && searchValue
              ? (
              <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                <img
                  src={ClearIcon}
                  alt="clear"
                  onClick={handleClear}
                  style={{ cursor: 'pointer' }}
                />
              </InputAdornment>
                )
              : null
        }}
      />
    </ClickAwayListener>
  )
}

export default Search
